import { ewiseCourseUrl } from "./starting-teacher-route.js";

export type StartingTeacherSource = {
  id: string;
  title: string;
  href: string;
};

/** Registrable host, without `www` and without the page path. */
export function sourceHost(href: string): string {
  return new URL(href).hostname.replace(/^www\./, "");
}

const page = (id: string, title: string, href: string): StartingTeacherSource => ({ id, title, href });

const course = (id: string, title: string, courseId: string): StartingTeacherSource => ({
  id,
  title: title.replace(/\s*\([^)]*\)\s*$/g, ""),
  href: ewiseCourseUrl(courseId, title),
});

const sourcesBySkill: Record<string, readonly StartingTeacherSource[]> = {
  kennisVak: [
    page(
      "basisvaardigheden",
      "Basisvaardigheden",
      "https://www.onderwijskennis.nl/basisvaardigheden",
    ),
    page(
      "taal-leidraad",
      "Betekenisvol en functioneel taalonderwijs in groep 3-8",
      "https://www.onderwijskennis.nl/sites/onderwijskennis/files/media-files/Taal-3-8-leidraad-ontwikkelkracht-NRO-1.1.pdf",
    ),
    course("woordenschat", "Werken aan de woordenschat", "1079283"),
  ],
  kennisCurriculum: [
    page("kerndoelen", "Kerndoelen primair onderwijs", "https://www.slo.nl/sectoren/po/kerndoelen/"),
    page(
      "kerndoelen-pdf",
      "Kerndoelen primair onderwijs (PDF)",
      "https://www.slo.nl/publish/pages/23186/kerndoelen-primair-onderwijs-slo-juli-2026.pdf",
    ),
    course(
      "leerdoelen",
      "E-WISE Extra: Motivatie vergroten met behulp van leerdoelen op het databord",
      "6164",
    ),
  ],
  instructie: [
    page(
      "expliciete-instructie",
      "Expliciete instructie inzetten",
      "https://www.onderwijskennis.nl/kennisbank/expliciete-instructie-inzetten",
    ),
    page(
      "directe-instructie-pdf",
      "Discussie over directe instructie (PDF)",
      "https://www.onderwijskennis.nl/sites/onderwijskennis/files/media-files/lr_bsm04_2024_discussie-over-directe-instructie.pdf",
    ),
    course("instructieprincipes", "10 instructieprincipes die elke leerkracht moet kennen", "1241972"),
  ],
  effect: [
    page(
      "formatief",
      "Formatief evalueren voor de leraar in het po",
      "https://www.slo.nl/thema/meer/formatief-evalueren/leraar-po/",
    ),
    page(
      "formatief-pdf",
      "Formatief evalueren in het po (PDF)",
      "https://www.slo.nl/publish/pages/14284/formatief-evalueren-in-het-po-werken-aan-groei.pdf",
    ),
    course("feedback", "Hoe zit het ook alweer met feedback geven?", "6110"),
  ],
  relatie: [
    page(
      "leerlingrelatie",
      "Leerkracht-leerlingrelaties in het primair onderwijs",
      "https://www.onderwijskennis.nl/kennisbank/leidraad-leerkracht-leerling-relatie-po",
    ),
    page(
      "klasklimaat",
      "Pedagogisch klasklimaat en groepsdynamiek",
      "https://www.onderwijskennis.nl/themas/pedagogisch-klasklimaat-en-groepsdynamiek",
    ),
    course(
      "kindercoaching",
      "Kindercoaching in de klas: echt in contact met je leerlingen (2025)",
      "1126744",
    ),
  ],
  klassen: [
    page(
      "klassenmanagement",
      "Klassenmanagement in het primair onderwijs",
      "https://www.onderwijskennis.nl/kennisbank/klassenmanagement-in-het-primair-onderwijs",
    ),
    page(
      "klassenmanagement-praktijk",
      "Klassenmanagement in de praktijk",
      "https://www.onderwijskennis.nl/kennisbank/klassenmanagement-in-de-praktijk",
    ),
    course("relaxed", "Relaxed voor de klas", "93849"),
  ],
  reflectie: [
    page(
      "bekwaamheidseisen",
      "Herijking bekwaamheidseisen leraar (PDF)",
      "https://www.aob.nl/assets/Downloads/Herijking-bekwaamheidseisen-leraar.pdf",
    ),
    course("meesterschap", "Meesterschap, over expertise in lesgeven", "1329807"),
  ],
  organisatie: [
    page(
      "bekwaamheidseisen",
      "Herijking bekwaamheidseisen leraar (PDF)",
      "https://www.aob.nl/assets/Downloads/Herijking-bekwaamheidseisen-leraar.pdf",
    ),
    course("generaties", "Omgaan met generaties op school", "956048"),
    course("gesprekken", "Deep democracy: écht goede gesprekken voeren", "42706"),
  ],
  omgeving: [
    page(
      "ouderbetrokkenheid",
      "Ouderbetrokkenheid",
      "https://www.onderwijskennis.nl/themas/ouderbetrokkenheid",
    ),
    course(
      "ouders",
      "Handelingsgericht werken: ouders en leerlingen betrekken als het moeilijk gaat",
      "42663",
    ),
    course(
      "meldcode",
      "Kindermishandeling: werk aan veiligheid met de meldcode (2026)",
      "1225805",
    ),
  ],
  beroep: [
    page(
      "bekwaamheidseisen",
      "Herijking bekwaamheidseisen leraar (PDF)",
      "https://www.aob.nl/assets/Downloads/Herijking-bekwaamheidseisen-leraar.pdf",
    ),
    course("meesterschap", "Meesterschap, over expertise in lesgeven", "1329807"),
    course("gesprekken", "Deep democracy: écht goede gesprekken voeren", "42706"),
  ],
};

export function startingTeacherSources(skillId: string): readonly StartingTeacherSource[] {
  return sourcesBySkill[skillId] ?? [];
}
